/**
 * Confluence zones: a translucent band across the chart where two or more key
 * levels stack, labelled on the left (the right edge belongs to the axis tags).
 *
 * Drawn under the candles - context, not price action. Hover/tap hit-testing
 * goes through `zoneAt`, same contract as the other primitives' `markerAt` /
 * `labelAt`: CSS pixels in, item out, null for empty space.
 */
import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import type { ConfluenceZone } from '../../utils/confluence';

const FILL = 'rgba(255, 90, 31, 0.13)';
const EDGE = 'rgba(255, 90, 31, 0.55)';
const TEXT = '#ff8a50';
/** A zone of identical prices still needs a visible band. */
const MIN_BAND_PX = 6;

interface Placed {
  top: number;
  bottom: number;
  zone: ConfluenceZone;
}

class ConfluenceRenderer implements IPrimitivePaneRenderer {
  private _placed: Placed[] = [];
  private _width = 0;
  private _label = '';

  update(placed: Placed[], width: number, label: string): void {
    this._placed = placed;
    this._width = width;
    this._label = label;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const h = scope.horizontalPixelRatio as number;
      const v = scope.verticalPixelRatio as number;
      for (const { top, bottom } of this._placed) {
        ctx.save();
        ctx.fillStyle = FILL;
        ctx.fillRect(0, top * v, this._width * h, (bottom - top) * v);
        ctx.strokeStyle = EDGE;
        ctx.lineWidth = 1 * v;
        ctx.setLineDash([3 * h, 3 * h]);
        ctx.beginPath();
        ctx.moveTo(0, top * v);
        ctx.lineTo(this._width * h, top * v);
        ctx.moveTo(0, bottom * v);
        ctx.lineTo(this._width * h, bottom * v);
        ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.font = `bold ${10 * v}px Arial`;
        ctx.textBaseline = 'bottom';
        const pad = 4 * h;
        const w = ctx.measureText(this._label).width;
        const y = top * v - 2 * v;
        ctx.fillStyle = 'rgba(11, 15, 25, 0.8)';
        ctx.fillRect(pad, y - 12 * v, w + pad * 2, 13 * v);
        ctx.fillStyle = TEXT;
        ctx.fillText(this._label, pad * 2, y);
        ctx.restore();
      }
    });
  }
}

class ConfluencePaneView implements IPrimitivePaneView {
  constructor(private _source: ConfluenceZonePrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'bottom';
  }
  renderer(): IPrimitivePaneRenderer {
    return this._source.buildRenderer();
  }
}

export class ConfluenceZonePrimitive implements ISeriesPrimitive<Time> {
  private _paneView = new ConfluencePaneView(this);
  private _renderer = new ConfluenceRenderer();
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _requestUpdate?: () => void;
  private _zones: ConfluenceZone[] = [];
  private _label = '';
  private _placed: Placed[] = [];

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
    this._placed = [];
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {}

  setZones(zones: ConfluenceZone[], label: string): void {
    this._zones = zones;
    this._label = label;
    this._requestUpdate?.();
  }

  getZones(): ConfluenceZone[] {
    return this._zones;
  }

  /** The zone under CSS-pixel `y`, with a few px of slack for thin bands. */
  zoneAt(_x: number, y: number): ConfluenceZone | null {
    for (const p of this._placed) {
      if (y >= p.top - 3 && y <= p.bottom + 3) return p.zone;
    }
    return null;
  }

  buildRenderer(): IPrimitivePaneRenderer {
    const series = this._series;
    const chart = this._chart;
    const placed: Placed[] = [];
    if (series && chart) {
      for (const zone of this._zones) {
        const yHigh = series.priceToCoordinate(zone.high);
        const yLow = series.priceToCoordinate(zone.low);
        if (yHigh === null || yLow === null) continue;
        let top = Math.min(yHigh, yLow);
        let bottom = Math.max(yHigh, yLow);
        if (bottom - top < MIN_BAND_PX) {
          const mid = (top + bottom) / 2;
          top = mid - MIN_BAND_PX / 2;
          bottom = mid + MIN_BAND_PX / 2;
        }
        placed.push({ top, bottom, zone });
      }
    }
    this._placed = placed;
    this._renderer.update(placed, chart ? chart.timeScale().width() : 0, this._label);
    return this._renderer;
  }
}
